import { ApiError } from '../errors/api-error.js';
import { withRetry } from '../utils/retry.js';

export class AssetGroupMeasureApplicabilityService {
    constructor(api, config = {}) {
        this.api = api;
        this.config = config;
    }

    async getAll(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getAll(params),
                { maxRetries: 2 },
            );

            return {
                success: true,
                data: response.data,
                pagination: response.pagination || {},
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch measure applicability list');
        }
    }

    async getById(id) {
        try {
            const response = await withRetry(
                () => this.api.getById(id),
                { maxRetries: 2 },
            );

            return {
                success: true,
                data: response.data,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch measure applicability');
        }
    }

    async getLinkedMeasures(id, params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getLinkedMeasures(id, params),
                { maxRetries: 2 },
            );

            return {
                success: true,
                data: response.data,
                pagination: response.pagination || {},
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch linked measures');
        }
    }

    async update(id, payload) {
        try {
            const response = await withRetry(
                () => this.api.patch(id, payload),
                { maxRetries: 2 },
            );

            return {
                success: true,
                data: response.data,
                message: response.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update measure applicability');
        }
    }

    async getMeasureEvaluation(id, measureId) {
        try {
            const response = await withRetry(
                () => this.api.getMeasureEvaluation(id, measureId),
                { maxRetries: 2 },
            );

            return {
                success: true,
                data: response.data,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch measure evaluation');
        }
    }

    async updateMeasureEvaluation(id, measureId, payload) {
        try {
            const response = await withRetry(
                () => this.api.patchMeasureEvaluation(id, measureId, payload),
                { maxRetries: 2 },
            );

            return {
                success: true,
                data: response.data,
                message: response.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update measure evaluation');
        }
    }

    async updateCurrentSituationMeasureEvaluation(id, measureId, payload) {
        try {
            const response = await withRetry(
                () => this.api.patchCurrentSituationMeasureEvaluation(id, measureId, payload),
                { maxRetries: 2 },
            );

            return {
                success: true,
                data: response.data,
                message: response.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update current situation measure evaluation');
        }
    }

    async getPriorMeasureEvaluations(id, measureId) {
        try {
            const response = await withRetry(
                () => this.api.getPriorMeasureEvaluations(id, measureId),
                { maxRetries: 2 },
            );

            return {
                success: true,
                data: response.data || [],
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch prior measure evaluations');
        }
    }

    async getPriorMeasureEvaluationDetail(id, measureId, sourceApplicabilityId) {
        try {
            const response = await withRetry(
                () => this.api.getPriorMeasureEvaluationDetail(id, measureId, sourceApplicabilityId),
                { maxRetries: 2 },
            );

            return {
                success: true,
                data: response.data,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch prior measure evaluation detail');
        }
    }

    async getPriorEvaluationRoadMapWorkPackages(id, measureId, sourceApplicabilityId, params = {}) {
        return this._fetchPaginated(
            () => this.api.getPriorEvaluationRoadMapWorkPackages(id, measureId, sourceApplicabilityId, params),
            'Failed to fetch prior evaluation road map work packages',
        );
    }

    async getPriorEvaluationCompensatoryControls(id, measureId, sourceApplicabilityId, params = {}) {
        return this._fetchPaginated(
            () => this.api.getPriorEvaluationCompensatoryControls(id, measureId, sourceApplicabilityId, params),
            'Failed to fetch prior evaluation compensatory controls',
        );
    }

    async _fetchPaginated(apiCall, fallbackMessage) {
        try {
            const response = await withRetry(() => apiCall(), { maxRetries: 2 });
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {},
            };
        } catch (error) {
            throw this._handleError(error, fallbackMessage);
        }
    }

    async getLinkedRoadMapWorkPackages(id, measureId, params = {}) {
        return this._fetchPaginated(
            () => this.api.getLinkedRoadMapWorkPackages(id, measureId, params),
            'Failed to fetch linked road map work packages',
        );
    }

    async getAvailableRoadMapWorkPackages(id, measureId, params = {}) {
        return this._fetchPaginated(
            () => this.api.getAvailableRoadMapWorkPackages(id, measureId, params),
            'Failed to fetch available road map work packages',
        );
    }

    async linkRoadMapWorkPackages(id, measureId, ids) {
        try {
            const response = await this.api.linkRoadMapWorkPackages(id, measureId, ids);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to link road map work packages');
        }
    }

    async unlinkRoadMapWorkPackage(id, measureId, workPackageId) {
        try {
            const response = await this.api.unlinkRoadMapWorkPackage(id, measureId, workPackageId);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink road map work package');
        }
    }

    async bulkUnlinkRoadMapWorkPackages(id, measureId, ids) {
        try {
            const response = await this.api.bulkUnlinkRoadMapWorkPackages(id, measureId, ids);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to bulk unlink road map work packages');
        }
    }

    async getLinkedCompensatoryControls(id, measureId, params = {}) {
        return this._fetchPaginated(
            () => this.api.getLinkedCompensatoryControls(id, measureId, params),
            'Failed to fetch linked compensatory controls',
        );
    }

    async getAvailableCompensatoryControls(id, measureId, params = {}) {
        return this._fetchPaginated(
            () => this.api.getAvailableCompensatoryControls(id, measureId, params),
            'Failed to fetch available compensatory controls',
        );
    }

    async linkCompensatoryControls(id, measureId, ids) {
        try {
            const response = await this.api.linkCompensatoryControls(id, measureId, ids);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to link compensatory controls');
        }
    }

    async unlinkCompensatoryControl(id, measureId, compensatoryControlId) {
        try {
            const response = await this.api.unlinkCompensatoryControl(id, measureId, compensatoryControlId);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink compensatory control');
        }
    }

    async bulkUnlinkCompensatoryControls(id, measureId, ids) {
        try {
            const response = await this.api.bulkUnlinkCompensatoryControls(id, measureId, ids);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to bulk unlink compensatory controls');
        }
    }

    _handleError(error, fallbackMessage) {
        if (error instanceof ApiError) return error;
        return new ApiError(fallbackMessage, error?.response?.status || 500, error?.response?.data);
    }
}
